import { useRef, useState } from 'react';
import specialityService from '@/services/contact/specialityService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { SpecialityModal } from '@/pages/contact';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { ColumnToggleDropdown } from '@/components/TableFilter/ColumnToggleDropdown';
import { Container } from '@/components/container';

DataTable.use(DT);

const Specialities = () => {
  const { logout } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [itemId, setItemId] = useState(null);
  const { ability } = useAbility();
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef({});
  const formikRef = useRef();
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const columns = [
    { data: 'name', title: 'Name', name: 'name' },
    {
      data: 'is_default',
      title: 'Type',
      name: 'is_default',
      render: (data) => (data ? 'Standard' : 'Custom'),
    },
    {
      data: 'contacts_count',
      title: 'Contacts',
      name: 'contacts_count',
      orderable: false,
      render: (data) => data ?? 0,
    },
    {
      title: 'Actions',
      name: 'actions',
      data: null,
      orderable: false,
      searchable: false,
      render: (data, type, row) => `<div id="speciality-action-cell-${row.id}"></div>`,
    },
  ];

  const defaultVisibleColumns = ['name', 'is_default', 'contacts_count', 'actions'];

  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? specialityService.updateSpeciality : specialityService.createSpeciality;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        handleModalClose();
        refresh();
        toast.success(`Specialty ${isEdit ? 'Updated' : 'Created'}`, {
          description: `${data.name} ${isEdit ? 'updated' : 'created'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const formattedErrors = Object.fromEntries(
          Object.entries(error.errors).map(([key, value]) => [key, value[0]])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: formattedErrors.name,
        });
      }
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status, data } = await specialityService.deleteSpeciality(actionId);
      if (status) {
        refresh();
        toast.success('Deleted Specialty.', {
          description: `${data.name} deleted successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      if (error.message?.includes('cannot be deleted')) {
        toast.error('Unable to delete Specialty.', { description: error.message });
        return;
      }
      toast.error('Oops! Something went wrong.', {
        description: 'An error occurred while deleting the specialty. Please try again in a moment.',
        action: {
          label: 'Ok',
        },
      });
    }
  };

  const handleEdit = async (id) => {
    setItemId(id);
    setOpen(true);
    setIsLoading(true);
    setMode('edit');
    try {
      const { data, status } = await specialityService.getSingleSpeciality(id);
      if (status) {
        setSelectedData(data);
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to fetch specialty.', {
        description: 'An error occurred while fetching the specialty. Please try again later.',
        action: {
          label: 'Ok',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setItemId(null);
    setSelectedData(null);
    setMode('create');
    setOpen(false);
  };

  // Standard entries ship with the app — the firm can use them but not change them.
  const renderCustomActions = (row) => {
    if (row.is_default) return null;

    return (
      <div className="flex items-center gap-3">
        <Can I="handle" a="contact" ability={ability}>
          <button title="Edit" onClick={() => handleEdit(row.id)} className="text-gray-500 hover:text-primary">
            <KeenIcon icon="pencil" />
          </button>
        </Can>
        <Can I="delete" a="contact" ability={ability}>
          <button title="Delete" onClick={() => openModal(row.id)} className="text-gray-500 hover:text-red-600">
            <KeenIcon icon="trash" />
          </button>
        </Can>
      </div>
    );
  };

  const ajaxConfig = createDataTableConfig('/contacts/specialities/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[0, 'asc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      rows.each((row) => {
        const container = document.getElementById(`speciality-action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
      });
    },
  });

  const refresh = () => {
    tableRef.current.dt().ajax.reload();
  };

  return (
    <Container>
      <div className="min-w-full mb-4 card">
        <div className="card-header">
          <div className='flex items-center gap-1'>
            <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            <Can I="handle" a="contact" ability={ability}>
              <button className='text-xs btn btn-sm btn-light' onClick={() => { setOpen(true) }}>
                <KeenIcon icon="plus" className="w-fit btn-icon" />
                Add New
              </button>
            </Can>
          </div>
        </div>

        <DataTable
          id="speciality-table"
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />

        <SpecialityModal
          open={open}
          onCloseModal={handleModalClose}
          handleMode={mode}
          data={selectedData}
          onSubmit={handleSubmit}
          createFieldLoading={isLoading}
          updateFieldLoading={loading}
          ref={formikRef}
        />

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this specialty? This action is permanent and cannot be undone."
          title="Are You Sure?"
        />
      </div>
    </Container>
  );
};

export { Specialities };
